/** The customer sources panel round (2026-11-04, brief `docs/briefs/customer-sources-panel-round.md` part one rule 3, Q2 as refined by the red
 *  team's finding 9): the browser remembers whether each source's section was left open, per workspace, per place (the ticket sidebar, the
 *  customer page) and per source. One entry per workspace, `ob-sources:<slug>`, holds `{ sidebar: { <source key>: open }, page: { … } }`. Folded
 *  in the sidebar and open on the customer page until the person changes it. A private window or blocked site data gives the defaults: reading
 *  never throws, and a write that cannot be kept is forgotten. Pure: the storage is passed in. */
export type SourcePlace = "sidebar" | "page";
type Memory = Partial<Record<SourcePlace, Record<string, boolean>>>;
const DEFAULT_OPEN: Record<SourcePlace, boolean> = { sidebar: false, page: true };

export const sourceOpenKey = (slug: string): string => `ob-sources:${slug}`;

function readMemory(storage: Storage | null, slug: string): Memory {
  try {
    const raw = storage?.getItem(sourceOpenKey(slug));
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" ? (parsed as Memory) : {};
  } catch { return {}; }
}

/** whether the source's section is open in that place: the remembered choice, else the place's default */
export function readSourceOpen(storage: Storage | null, slug: string, place: SourcePlace, source: string): boolean {
  const v = readMemory(storage, slug)[place]?.[source];
  return typeof v === "boolean" ? v : DEFAULT_OPEN[place];
}

/** remember the person's choice for that place and source; the other place's choices stay */
export function writeSourceOpen(storage: Storage | null, slug: string, place: SourcePlace, source: string, open: boolean): void {
  if (!storage) return;
  try {
    const m = readMemory(storage, slug);
    storage.setItem(sourceOpenKey(slug), JSON.stringify({ ...m, [place]: { ...m[place], [source]: open } }));
  } catch { /* private mode: the choice is forgotten */ }
}
